/**
 * CreateRecordFromKsefModal — dekretacja faktury KSeF do modułu custom (Faza 5).
 *
 * Wybór modułu docelowego → POST /ksef/invoices/[id]/create-record (auto-mapowanie pól
 * faktury na pola modułu po slugu; backend linkuje fakturę i ustawia status „booked").
 *
 * Teksty PL inline (konwencja gałęzi roboczej — ekstrakcja i18n przed docelowym mergem).
 */

import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { Button } from '@/components/Button';
import { Modal } from '@/components/Modal';
import { SearchableSelect } from '@/components/SearchableSelect';
import { useToast } from '@/contexts/ToastContext';
import { api } from '@/lib/api';

interface ModuleListItem {
  id: string;
  name: string;
  slug: string;
}

interface Props {
  invoiceId: string;
  /** Podpis faktury (numer KSeF / kontrahent) do nagłówka modala. */
  invoiceLabel?: string;
  /** Kierunek faktury — decyduje o etykiecie celu wbudowanego (zakup/sprzedaż). */
  direction?: string;
  onClose: () => void;
  /** Po utworzeniu — trasa nowego rekordu (faktura wbudowana albo rekord modułu), żeby
   *  wołający od razu go otworzył (zgłoszenie ze szkolenia 14.10: rekord był widoczny dopiero
   *  po odświeżeniu). `null` = serwer nie zwrócił identyfikatora. */
  onCreated: (recordRoute: string | null) => void;
}

interface CreateRecordResponse {
  /** Cel wbudowany (faktura zakupu/sprzedaży). */
  documentId?: string;
  documentClass?: string;
  /** Cel = moduł własny. */
  recordId?: string;
  moduleId?: string;
}

/** Trasa karty utworzonego rekordu — mirror `openLinkedRecord` w KsefInvoices. */
export function createdRecordRoute(data: CreateRecordResponse): string | null {
  if (data.documentId) {
    return data.documentClass === 'sale_invoice'
      ? `/sale-invoices/${data.documentId}`
      : `/purchase-invoices/${data.documentId}`;
  }
  if (data.recordId && data.moduleId) return `/modules/${data.moduleId}/${data.recordId}`;
  return null;
}

const BUILTIN_INVOICE = 'builtin:invoice';

export function CreateRecordFromKsefModal({
  invoiceId,
  invoiceLabel,
  direction,
  onClose,
  onCreated,
}: Props) {
  const toast = useToast();
  const qc = useQueryClient();
  const [moduleId, setModuleId] = useState('');

  const modulesQuery = useQuery({
    queryKey: ['custom-modules-for-ksef'],
    queryFn: async () => {
      const res = await api.get<{ modules: ModuleListItem[] }>('/modules');
      return res.data.modules ?? [];
    },
  });
  const modules = modulesQuery.data ?? [];

  const createMutation = useMutation({
    mutationFn: async () => {
      const res = await api.post<CreateRecordResponse>(
        `/ksef/invoices/${invoiceId}/create-record`,
        { moduleId },
      );
      return res.data;
    },
    onSuccess: (data) => {
      toast.show({
        title: 'Utworzono rekord i oznaczono fakturę jako pobraną',
        variant: 'success',
      });
      qc.invalidateQueries({ queryKey: ['ksef-invoices'] });
      qc.invalidateQueries({ queryKey: ['ksef-invoice', invoiceId] });
      onCreated(createdRecordRoute(data));
    },
    onError: (err) => {
      const code =
        err && typeof err === 'object' && 'response' in err
          ? ((err as { response?: { data?: { error?: string } } }).response?.data?.error ?? '')
          : '';
      toast.show({
        title:
          code === 'ALREADY_LINKED'
            ? 'Faktura ma już powiązany rekord.'
            : 'Nie udało się utworzyć rekordu.',
        variant: 'error',
      });
    },
  });

  // `Modal` renderuje się w portalu do <body>: okno otwiera się z drawera podglądu (też portal,
  // z-50) — w drzewie strony lądowałoby POD nim. Montowane później → leży nad drawerem.
  return (
    <Modal
      open
      onClose={onClose}
      title="Utwórz rekord z faktury"
      description={invoiceLabel}
      size="sm"
      testId="create-record-from-ksef-modal"
      // Tworzenie rekordu w toku — tło i Esc nie zamykają okna (X i „Anuluj" działają).
      closeOnBackdrop={!createMutation.isPending}
      closeOnEsc={!createMutation.isPending}
      footer={
        <>
          <Button variant="secondary" size="sm" onClick={onClose}>
            Anuluj
          </Button>
          <Button
            variant="primary"
            size="sm"
            disabled={!moduleId}
            loading={createMutation.isPending}
            onClick={() => createMutation.mutate()}
          >
            Utwórz rekord
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        <p className="text-text-secondary">
          Dane faktury (numer, kontrahent, kwoty, daty) zostaną automatycznie przeniesione. Fakturę
          oznaczymy jako pobraną.
        </p>

        {modulesQuery.isLoading && <p className="text-text-muted">Ładowanie…</p>}

        {!modulesQuery.isLoading && (
          <label className="block">
            <span className="mb-1 block text-xs font-medium uppercase text-text-muted">
              Cel dekretacji
            </span>
            <SearchableSelect
              value={moduleId || null}
              onChange={(value) => setModuleId(value ?? '')}
              placeholder="— wybierz cel —"
              aria-label="Cel dekretacji"
              data-testid="ksef-create-record-target"
              options={[
                {
                  value: BUILTIN_INVOICE,
                  label:
                    direction === 'sales'
                      ? 'Faktura sprzedaży (moduł wbudowany)'
                      : 'Faktura zakupu (moduł wbudowany)',
                },
                ...modules.map((m) => ({
                  value: m.id,
                  label: m.name,
                  description: 'Moduł własny',
                })),
              ]}
            />
          </label>
        )}
      </div>
    </Modal>
  );
}

export default CreateRecordFromKsefModal;
